// "N contact issues" pill beside the group tabs. Click opens a popover listing
// contact data gaps (missing email / mobile) that may block policies in this group.

import { Link } from "react-router-dom";
import { ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";

import { useContactDataWarnings } from "../../Molecules/ContactDataWarning";

export function GroupSetupIssues() {
  const {
    group,
    emailWarning,
    mobileWarning,
    onClickNoEmail,
    onClickNoPhone,
  } = useContactDataWarnings();

  const plural = (n: number) => n === 1 ? `contact` : `contacts`;

  const issues = [
    emailWarning && {
      title: `${group.contactsNoEmailCount} ${plural(group.contactsNoEmailCount)} missing email`,
      description: `Required for email policies in this group.`,
      onView: onClickNoEmail,
    },
    mobileWarning && {
      title: `${group.contactsNoMobileCount} ${plural(group.contactsNoMobileCount)} missing mobile`,
      description: `Required for SMS policies in this group.`,
      onView: onClickNoPhone,
    },
  ].filter(Boolean) as { title: string; description: string; onView: () => void }[];

  if (!issues.length) return null;

  return (
    <Popover className={ `relative flex-none` }>
      <PopoverButton className={ `inline-flex items-center gap-1.5 rounded-full border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs font-semibold text-yellow-800 shadow-sm hover:bg-yellow-100 focus:outline-none cursor-pointer` }>
        <ExclamationTriangleIcon className={ `h-3.5 w-3.5 text-yellow-400` }
          aria-hidden={ `true` } />
        { `${issues.length} contact ${issues.length === 1 ? `issue` : `issues`}` }
        <ChevronDownIcon className={ `h-3 w-3` } />
      </PopoverButton>
      <PopoverPanel
        anchor={ `bottom end` }
        className={ `z-20 w-[340px] rounded-lg border border-gray-200 bg-white shadow-xl [--anchor-gap:6px]` }
      >
        <div className={ `px-4 py-3 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500` }>
          { `Contact issues` }
        </div>
        { issues.map(issue => (
          <div
            key={ issue.title }
            className={ `flex gap-2.5 px-4 py-3 border-b border-gray-100` }
          >
            <ExclamationTriangleIcon className={ `h-3.5 w-3.5 mt-0.5 flex-none text-yellow-400` }
              aria-hidden={ `true` } />
            <div>
              <div className={ `text-sm font-semibold text-gray-900` }>{ issue.title }</div>
              <p className={ `text-xs text-gray-500 mt-0.5` }>
                { issue.description }
                { ` ` }
                <button
                  type={ `button` }
                  onClick={ issue.onView }
                  className={ `font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-800 cursor-pointer` }
                >
                  { `View contacts` }
                </button>
              </p>
            </div>
          </div>
        )) }
        <div className={ `px-4 py-2.5 flex justify-end` }>
          <Link
            to={ `contacts` }
            className={ `text-xs font-semibold text-white bg-paidniceGreen-600 hover:bg-paidniceGreen-700 rounded-md px-3 py-1.5 cursor-pointer` }
          >
            { `Review contacts` }
          </Link>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
